import { getCategoryCatalog } from '../catalog/categories';
import { resolveAttributesTree } from './attributes';
import { snapToSocket, checkBounds, checkSupport } from './sockets';
import { validateRequiredParts } from './rules/common';
import { validateCategoryRules } from './rules/categories';

export function updateChildren(parts, changedPartId) {
  if (!changedPartId) return parts;
  const map = new Map(parts.map((p) => [p.id, p]));
  const parent = map.get(changedPartId);
  if (!parent) return parts;
  return parts.map((part) => {
    if (part.parentId !== changedPartId) return part;
    const pb = parent.localBox || {};
    const cb = part.localBox || {};
    const nextW = Math.min(Number(cb.w || 0), Math.max(0, Number(pb.w || 0)));
    const nextH = Math.min(Number(cb.h || 0), Math.max(0, Number(pb.h || 0)));
    return {
      ...part,
      localBox: { ...cb, w: nextW, h: nextH },
    };
  });
}

export function validatePlacement(source, { mode = 'soft', changedPartId = null } = {}) {
  if (!source || source.kind !== 'parametric-v2') {
    return { source, issues: [], mode, changed: false };
  }
  const catalog = getCategoryCatalog(source.category);
  const withAttrs = resolveAttributesTree(source);
  let parts = withAttrs.parts || [];
  const root = parts.find((p) => !p.parentId) || null;
  if (root) {
    // Safety normalization: orphan parts are reattached to the root body
    // so bounds/sockets checks are always applied.
    parts = parts.map((p) => {
      if (!p.parentId || p.id === root.id) return p;
      const hasParent = parts.some((x) => x.id === p.parentId);
      return hasParent ? p : { ...p, parentId: root.id };
    });
  }
  if (changedPartId) parts = updateChildren(parts, changedPartId);

  const partById = new Map(parts.map((p) => [p.id, p]));
  const issues = [...validateRequiredParts({ ...withAttrs, parts }, catalog)];
  const snapped = parts.map((part) => {
    const parent = part.parentId ? partById.get(part.parentId) : null;
    const clamped = parent ? snapToSocket(part, parent) : part;
    if (parent) {
      const bounds = checkBounds(clamped, parent);
      const support = checkSupport(clamped, parent);
      if (!bounds.ok) issues.push({ level: mode, partId: part.id, ...bounds });
      if (!support.ok) issues.push({ level: mode, partId: part.id, ...support });
    }
    return clamped;
  });
  const categoryIssues = validateCategoryRules({ ...withAttrs, parts: snapped });
  issues.push(...categoryIssues);

  return {
    source: { ...withAttrs, parts: snapped },
    issues,
    mode,
    changed: JSON.stringify(snapped) !== JSON.stringify(source.parts || []),
  };
}
